/**
 * RWBR — RACE WEEK: the story engine.
 *
 * SOURCE: the RACE WEEK Events Guide booklet that ships with the game, pages
 * 2-4 (procedure) and 6-10 (the five charts). Cited below as `RWEG/p.N`. The
 * charts themselves live in `src/data/race-week-charts.json`, transcribed
 * verbatim; this file is the procedure that reads them.
 *
 * WHAT THIS PRODUCES. Race Week is not a qualifying minigame that spits out a
 * grid. It is where the season's *story* is generated — the feuds, the fines,
 * the suspensions, the injuries, the interviews and the one race a season where
 * somebody does not come home. The grid is a side-effect. So this module
 * returns the grid AND every consequence, each one carrying the page that
 * sanctions it.
 *
 * THE BOOKLET'S OWN FRAMING (RWEG/p.2, verbatim):
 *   "Every race has to have a qualifying event, right?  Rather than roll dice
 *    for 50-60 drivers, RED WHITE & BLUE RACIN's 'Race Week' uses a system that
 *    quickly approximates each driver's race week results by shuffling the
 *    driver cards and dealing each card randomly into one of four categories..."
 *
 * That sentence is why `field.ts` builds a LICENSED ROSTER of ~53 and not a
 * 43-car field. 43 is what STEP SIX counts UP TO; it is the maximum starting
 * field, never the size of the entry list. Drivers left on the bubble stack
 * when the field reaches 43 simply do not race.
 *
 * THE SIX STEPS (RWEG/p.2-4):
 *   1  Set out the boards, pick a track, shuffle the driver cards.
 *   2  TV LIST      — 2d6 cards, each rolled on the RACE WEEK TV LIST CHART.
 *   3  FAST WEEK    — 2d6 cards, placed by trophy, no die roll.
 *   4  SLOW WEEK    — 2d6 cards, placed by trophy, no die roll.
 *   5  UNEVENTFUL   — everyone left, placed by trophy, no die roll.
 *   6  Round out the field to 43 off the bubble stack.
 *
 * THE DICE. Red + white make the 11-66 chart index; the blue die selects the
 * box inside a group on a PLACEMENT reading (RWEG/p.3, and confirmed against
 * the boards' identical 11-66 DRIVER FINDER convention). The two metallic dice
 * are read as a 2-12 SUM for the three stack counts. `dice.ts` keeps those
 * notations apart on purpose and so does this module.
 *
 * PURITY. `runRaceWeek` builds its own seeded `Dice`, reads nothing off disk at
 * run time (the charts are imported JSON), mutates none of its arguments, and
 * returns every roll it made in the ledger.
 */

import type { DriverCard, FeudEntry, Quality, RaceState, Trophy } from './types.ts';
import { Dice, makeRng } from './dice.ts';
import type { DieDraw } from './dice.ts';
import type { DiceRecord, Modifier, PositionDelta, RuleEvent } from './ruleEvent.ts';
import { renumber } from './ruleEvent.ts';
import type { RaceSetup } from './race.ts';
import type { TrackCard } from './turn.ts';
import chartsJson from '../data/race-week-charts.json';

// ---------------------------------------------------------------------------
// Citations
// ---------------------------------------------------------------------------

export const CITE_SETUP = 'RWEG/p.2 — STEP ONE (shuffle the driver cards)';
export const CITE_TV_LIST = 'RWEG/p.2 — STEP TWO (TV LIST)';
export const CITE_TV_GOLD =
  'RWEG/p.2 — STEP TWO: "the FIRST driver on the TV List is always considered to have a GOLD TROPHY symbol, whether he has one or not"';
export const CITE_PLACEMENT = 'RWEG/p.3 — PLACEMENT A/B/C/D';
export const CITE_CARRYOVER =
  'RWEG/p.3 — "certain drivers from the previous week\'s race may make the next week\'s TV LIST, without rolling on the TV LIST charts"';
export const CITE_FEUD_NOTE =
  'RWEG/p.3 — "Certain TV LIST readings result in driver altercations during qualifying week"';
export const CITE_FAST_WEEK = 'RWEG/p.3 — STEP THREE (FAST WEEK)';
export const CITE_SLOW_WEEK = 'RWEG/p.3 — STEP FOUR (SLOW WEEK)';
export const CITE_UNEVENTFUL = 'RWEG/p.4 — STEP FIVE (UNEVENTFUL WEEK)';
export const CITE_ROUND_OUT = 'RWEG/p.4 — STEP SIX (round out the field to 43)';
export const CITE_OPEN_SPACES =
  'RWEG/p.4 — clarification: "if there may be open spaces in the TOP and/or MIDDLE groups… move up the highest-ranked drivers from the lower groups"';
export const CITE_GROUP_FULL =
  'RWEG/p.4 — clarification: "if the RACE WEEK booklet calls for placement into a group that\'s already full, simply place the driver in the next-best available position"';
export const CITE_ALTERCATION =
  'RWEG/p.4 — clarification: "Any drivers involved in an \'altercation\' during race week should be placed on the FEUD list"';
export const CITE_FUME =
  'RWEG/p.4 — clarification: "You may, at your discretion, place a driver on the FUME list as a result of his RACE WEEK result"';
export const CITE_CHIP_BAN =
  'RWEG/p.4 — "performances chips cannot be carried by drivers in the BOTTOM group or \'bubble\' stack… he forfiets his performance chip"';
export const CITE_DRIVER_FINES = 'SAG/31 — DRIVER FINES';
export const CITE_TEAM_FINES = 'SAG/32 — TEAM FINES';

const CHART_PAGE: Record<ChartId, string> = {
  'race-week-tv-list': 'RWEG/p.6',
  'tv-interviews': 'RWEG/p.7',
  'tv-event-x': 'RWEG/p.8',
  'tv-event-y': 'RWEG/p.9',
  'tv-event-z': 'RWEG/p.10',
};

/** `RWEG/p.10 — TV EVENT Z 13`. */
export function citeRow(chart: ChartId, code: string): string {
  return `${CHART_PAGE[chart]} — ${CHARTS[chart].name} ${code}`;
}

// ---------------------------------------------------------------------------
// Field geometry and configuration constants
// ---------------------------------------------------------------------------

/**
 * The maximum STARTING field. STEP SIX counts UP to this off the bubble stack;
 * nothing in Race Week may ever exceed it. Confirmed three ways: RWEG/p.4
 * ("bring the field to 43 drivers"), Board A/B geometry (1 + 6 + 12 = 19) and
 * the BOTTOM GROUP DRIVER FINDER's 24 entries (19 + 24 = 43).
 */
export const MAX_STARTING_FIELD = 43;

/** Board A, positions 2-7, selected by the blue die 1-6. */
export const TOP_SEATS = 6;
/** Board B, positions 8-19, six boxes of two (odd/even halves). */
export const MIDDLE_SEATS = 12;
/** Seats per MIDDLE box — the blue die picks the box, not the seat. */
export const MIDDLE_SEATS_PER_BOX = 2;

/** A dice notation, so `[dice]` is a setting and not a magic number. */
export interface DiceSpec {
  count: number;
  sides: number;
}

/**
 * RULING (D-RW-1). The booklet prints the literal token `[dice]` for the length
 * of a suspension (TV EVENT Z 12/32/42/52), for the wait before a quitting
 * driver signs elsewhere (Z 16) and, by extension, for any injury whose length
 * is not printed. NOTHING in the corpus defines it — cross_reference §B1
 * searched every source and found eleven uses and zero definitions.
 *
 * The ruling is **1d6**, exposed here so it is one edit away from being
 * something else. 1d6 is chosen because every other unprinted length in the
 * game's own family of charts is a single die, and because 2d6 would routinely
 * cost a driver a fifth of a 36-race season for a rules infraction.
 */
export const DICE_RACES: DiceSpec = { count: 1, sides: 6 };

// ---------------------------------------------------------------------------
// The charts
// ---------------------------------------------------------------------------

export type ChartId =
  | 'race-week-tv-list'
  | 'tv-interviews'
  | 'tv-event-x'
  | 'tv-event-y'
  | 'tv-event-z';

export const CHART_IDS: readonly ChartId[] = [
  'race-week-tv-list',
  'tv-interviews',
  'tv-event-x',
  'tv-event-y',
  'tv-event-z',
];

export type PlacementLetter = 'A' | 'B' | 'C' | 'D';

export interface RaceWeekEffect {
  type: string;
  [key: string]: unknown;
}

export interface RaceWeekChartEntry {
  /** The printed line, verbatim, typos and all. */
  text: string;
  effects: RaceWeekEffect[];
  placement: PlacementLetter | null;
}

export interface RaceWeekChart {
  id: ChartId;
  name: string;
  sourceCitation: string;
  dice: string;
  subtitle: string;
  entries: Record<string, RaceWeekChartEntry>;
}

type ChartsFile = Record<ChartId, RaceWeekChart> & { '$schema-note': string };

/** Imported, never read from disk at run time. */
export const CHARTS = chartsJson as unknown as ChartsFile;

/** Every legal 11-66 two-digit code, in chart order. */
export const CODES_11_66: readonly string[] = (() => {
  const out: string[] = [];
  for (let tens = 1; tens <= 6; tens++) for (let ones = 1; ones <= 6; ones++) out.push(`${tens}${ones}`);
  return out;
})();

export function chartEntry(chart: ChartId, code: string): RaceWeekChartEntry {
  const entry = CHARTS[chart]?.entries[code];
  if (!entry) throw new Error(`no entry ${code} on chart ${chart}`);
  return entry;
}

/** The `[sponsor]`, `[other driver]`, `[race day]` … tokens in an interview. */
export function tokensIn(text: string): string[] {
  return [...text.matchAll(/\[[^\]]+\]/g)].map((m) => m[0]);
}

/**
 * The substitutable tokens the TV INTERVIEWS chart actually uses. Kept as data
 * so a narration layer can bind them without re-deriving the list.
 */
export const INTERVIEW_TOKENS: readonly string[] = [
  '[sponsor]',
  '[Sponor]', // the booklet's own typo at INT 24, preserved deliberately
  '[driver]',
  '[other driver]',
  '[crew chief]',
  '[crew member]',
  '[this track]',
  '[race day]',
];

// ---------------------------------------------------------------------------
// Options and inputs
// ---------------------------------------------------------------------------

export interface RaceWeekOptions {
  /**
   * TV EVENT Z 13 — "Driver dies in wreck during qualifying." Implemented as
   * printed. DEFAULT TRUE: it is one row in 46,656 (a Z reading at all is
   * ~1 race in 31, and 13 is 1 in 36 of those), it is the single most
   * consequential thing the booklet can say, and a season that cannot lose a
   * driver is not the game that was published.
   *
   * Set false and the reading is suppressed and re-rolled on TV EVENT Z.
   */
  allowDriverDeath?: boolean;
  /** The starting field to round out to. Default 43; never exceeded. */
  fieldSize?: number;
  /** `[dice]` for suspensions and unprinted injury lengths. Default 1d6. */
  diceRaces?: DiceSpec;
  /**
   * RWEG/p.3 — drivers who make the TV LIST without rolling: the previous
   * race winner, drivers whose post-race comments made the sports shows, and
   * drivers whose feud carried over. Additive to the 2d6 count.
   */
  carryOverTvIds?: readonly string[];
  /** Feuds still live from last week. Carried into the result untouched. */
  carryOverFeuds?: readonly FeudEntry[];
  /**
   * EZRA-S2 §4.0 RC — is this week's race at a ROAD COURSE?
   *
   * Race Week did not previously know anything about the track, and for every
   * other rule it does not need to. The Road Course Expert is the only trait
   * whose effect lands during QUALIFYING and depends on where the race is, so
   * the season loop hands this one fact down.
   */
  roadCourse?: boolean;
  /** Drivers already unavailable (a suspension or injury from a previous week). */
  unavailableIds?: readonly string[];
  /**
   * Which `Quality` a TV card grants. See D-RW-2 below. Default: the driver's
   * own first quality, so being on the TV list grants the TV flag and no extra
   * rating.
   */
  tvQualityFor?: (card: DriverCard) => Quality;
}

export interface RaceWeekInput {
  /** The LICENSED ROSTER — every driver eligible to attempt this race (~53). */
  roster: readonly DriverCard[];
  seed: number;
  options?: RaceWeekOptions;
  /**
   * The dice to roll. Defaults to a fresh `Dice(seed)`, which is the whole of
   * the normal path. Supplied only when the faces have to be known in advance —
   * a scripted die in a test, or a shared die if a caller ever wants one race
   * week and one race to come off the same stream.
   *
   * The shuffle in STEP ONE never comes off this: it runs on its own stream
   * derived from `seed`, so scripting the charts does not also script the deal.
   */
  dice?: Dice;
}

// ---------------------------------------------------------------------------
// Consequences
// ---------------------------------------------------------------------------

export type UnavailableReason =
  | 'injury'
  | 'season-injury'
  | 'car-destroyed'
  | 'arrested'
  | 'suspended'
  | 'dead'
  | 'quit'
  | 'carried-over';

export interface UnableToRace {
  driverId: string;
  reason: UnavailableReason;
  /** The printed line that did it, or a note for a carried-over absence. */
  text: string;
  chart: ChartId | null;
  code: string | null;
  ruleCitation: string;
  /** Z 31 only: "team withdraws from race". */
  teamWithdrew?: boolean;
}

export interface Fine {
  driverId: string;
  /** Millions of bucks. `null` where the booklet says "fined" and prints no amount. */
  amount: number | null;
  currency: string;
  payer: 'driver' | 'team';
  offense: string;
  chart: ChartId;
  code: string;
  ruleCitation: string;
}

export interface PointsPenalty {
  driverId: string;
  /** The booklet prints "[and loss of points]" with no number. */
  scope: 'this-race';
  chart: ChartId;
  code: string;
  ruleCitation: string;
}

export interface Suspension {
  driverId: string;
  races: number;
  /** True — sanctions issued during race week cost him this race first. */
  includesThisRace: boolean;
  chart: ChartId;
  code: string;
  ruleCitation: string;
}

export interface Injury {
  driverId: string;
  /** `this-race` = "cannot race this week"; `season` = "rest of season". */
  duration: 'this-race' | 'season';
  chart: ChartId;
  code: string;
  ruleCitation: string;
}

export interface TeamTransfer {
  driverId: string;
  /** Weeks before he signs with the new team. */
  weeks: number;
  fromTeamId?: string;
  chart: ChartId;
  code: string;
  ruleCitation: string;
}

export interface ChipForfeit {
  driverId: string;
  chips: number;
  reason: string;
  ruleCitation: string;
}

export interface Interview {
  driverId: string;
  code: string;
  /** The quotable line, verbatim, tokens unsubstituted. */
  text: string;
  tokens: string[];
  placement: PlacementLetter;
}

/** One reading, including every chart it chained through. */
export interface ChartReading {
  chart: ChartId;
  code: string;
  text: string;
}

export interface TvListResult {
  driverId: string;
  /** TV LIST first, then X / Y / Z or the interview, in the order rolled. */
  chain: ChartReading[];
  /** The row that finally spoke. */
  chart: ChartId;
  code: string;
  text: string;
  placement: PlacementLetter | null;
  /** The blue die that came with the throw that produced the final reading. */
  blue: number;
  /** He was pulled in as the other half of an altercation and did not roll. */
  drawnAsPartner: boolean;
}

// ---------------------------------------------------------------------------
// The result
// ---------------------------------------------------------------------------

/**
 * Which of RWEG/p.2's four stacks a driver was dealt into. A carry-over TV
 * driver is NOT a fifth category — he is on the TV LIST without a roll and is
 * still dealt into one of these four (see `carryOverTvIds`).
 */
export type WeekCategory = 'tv' | 'fast' | 'slow' | 'uneventful';

export interface RaceWeekResult {
  seed: number;
  /**
   * D-018 — every face this WEEK was run on, in order, each marked `hand` or
   * `engine`. The exact mirror of `RaceResult.diceStream`, and here for exactly
   * the same reason.
   *
   * NULL FOR A SEEDED WEEK, on purpose: the seed IS the stream (D-005), so
   * logging forty objects a week × thirty-six weeks would record something the
   * record already contains.
   *
   * ⚠ IT WAS ADDED THE DAY THE WEEK BECAME HAND-ROLLABLE, and the gap it closes
   * is the one D-018 was written to prevent. The moment a player could roll
   * qualifying, the TV list and the feuds himself, those faces existed nowhere
   * on earth once he picked the dice up — the race kept its provenance and the
   * week threw its own away. A record that keeps half a weekend is not a record
   * of the weekend.
   */
  diceStream: readonly DieDraw[] | null;
  /** Every roster driver, in the order the shuffled stack deals them. */
  rosterOrder: string[];
  /** The starting grid, index 0 = pole sitter. Never longer than `fieldSize`. */
  grid: DriverCard[];
  /** `RaceSetup.tvList` — the temporary TV quality, by driver id. */
  tvList: Record<string, Quality>;
  /** TV LIST membership, in list order. */
  tvDriverIds: string[];
  /**
   * The subset of `tvDriverIds` who were already on television and joined
   * without a TV LIST roll (RWEG/p.3). They have no entry in `tvResults`.
   */
  carryOverTvIds: string[];
  /** Every TV LIST reading, in the order it was rolled. */
  tvResults: TvListResult[];
  /** The quotable dialogue this week produced. */
  interviews: Interview[];
  feuds: FeudEntry[];
  /** Fuming drivers who are actually on the grid — ready for `RaceSetup`. */
  fuming: string[];
  /** Which of the four stacks each driver was dealt into. */
  categories: Record<string, WeekCategory>;
  /** The PLACEMENT letter a driver's week ended at, where he got one. */
  placements: Record<string, PlacementLetter>;
  /** Failed to qualify. */
  dnq: string[];
  /** The bubble stack, highest-ranked first, after STEP SIX took its cut. */
  bubble: string[];
  /** Bubble drivers the field did not need. They do not race. */
  bubbleUnused: string[];
  unableToRace: UnableToRace[];
  fines: Fine[];
  pointsPenalties: PointsPenalty[];
  suspensions: Suspension[];
  injuries: Injury[];
  teamTransfers: TeamTransfer[];
  /** Chips carried to the green flag, by driver id. Never a BOTTOM/bubble car. */
  chips: Record<string, number>;
  chipsForfeited: ChipForfeit[];
  /** TV EVENT Z 21/41/61 cancelled qualifying. */
  badWeather: boolean;
  /** The full ledger, in the order the week happened. */
  events: RuleEvent[];
}

// ---------------------------------------------------------------------------
// The developing board
// ---------------------------------------------------------------------------

type SeatGroup = 'pole' | 'top' | 'middle' | 'bottom';

type SeatRef =
  | { group: 'pole' }
  | { group: 'top'; index: number }
  | { group: 'middle'; index: number }
  | { group: 'bottom' };

/**
 * Exported ONLY so `resolvePlacement` can be tested directly.
 *
 * That is not a convenience: EZRA RC's gold branch (D-EZ-25) was measured at
 * ZERO occurrences in 40 seasons, so it cannot be reached through `runRaceWeek`
 * in any reasonable number of seeds. **A rule with a rate that low can only be
 * unit-tested at the seam, or it is not tested at all.**
 */
export interface Board {
  pole: string | null;
  /** Positions 2-7. Index i is the blue die face i+1. */
  top: (string | null)[];
  /** Positions 8-19. Blue die face f owns indices 2f-2 and 2f-1. */
  middle: (string | null)[];
  /**
   * Positions 20+, in RUNNING order — index 0 is 20th, the highest-ranked car
   * in the group. RWEG/p.4 notes the physical stack is kept in reverse, "the
   * TOP driver on the BOTTOM group stack is actually the LAST driver in the
   * group"; that is a card-handling convenience, and reversing it here would
   * only make every read backwards.
   */
  bottom: string[];
  /** The bubble stack, highest-ranked first. */
  bubble: string[];
  dnq: string[];
}

export function newBoard(): Board {
  return {
    pole: null,
    top: Array<string | null>(TOP_SEATS).fill(null),
    middle: Array<string | null>(MIDDLE_SEATS).fill(null),
    bottom: [],
    bubble: [],
    dnq: [],
  };
}

const SEAT_ORDER: readonly SeatGroup[] = ['pole', 'top', 'middle', 'bottom'];

/**
 * RWEG/p.4 — "if the RACE WEEK booklet calls for placement into a group that's
 * already full, simply place the driver in the next-best available position."
 *
 * AMBIGUITY (D-RW-3): "next-best" is not given a direction. Reading it as
 * "search UPWARD" would let a driver sent to the BOTTOM group be promoted into
 * a hole in the TOP group, which turns every punishment into a reward. Reading
 * it as "the best position still available AT OR BELOW the group that was
 * called" preserves the ordering the four PLACEMENTs exist to express and still
 * satisfies the printed words. That is the reading implemented. A driver with
 * nothing available at or below him goes to the bubble stack.
 */
function firstFreeSeatFrom(board: Board, from: SeatGroup, bottomCap: number): SeatRef | null {
  for (let i = SEAT_ORDER.indexOf(from); i < SEAT_ORDER.length; i++) {
    const group = SEAT_ORDER[i];
    if (group === 'pole' && board.pole === null) return { group: 'pole' };
    if (group === 'top') {
      const index = board.top.indexOf(null);
      if (index !== -1) return { group: 'top', index };
    }
    if (group === 'middle') {
      const index = board.middle.indexOf(null);
      if (index !== -1) return { group: 'middle', index };
    }
    if (group === 'bottom' && board.bottom.length < bottomCap) return { group: 'bottom' };
  }
  return null;
}

/** The blue die's box in the MIDDLE group holds two seats (Board B, odd/even). */
function middleBoxSeats(blue: number): number[] {
  const first = (blue - 1) * MIDDLE_SEATS_PER_BOX;
  return Array.from({ length: MIDDLE_SEATS_PER_BOX }, (_, i) => first + i);
}

function seat(board: Board, ref: SeatRef, driverId: string): void {
  if (ref.group === 'pole') board.pole = driverId;
  else if (ref.group === 'top') board.top[ref.index] = driverId;
  else if (ref.group === 'middle') board.middle[ref.index] = driverId;
  else board.bottom.push(driverId);
}

function describeSeat(ref: SeatRef): string {
  if (ref.group === 'pole') return 'the POLE';
  if (ref.group === 'top') return `the TOP group, box ${ref.index + 1}`;
  if (ref.group === 'middle') return `the MIDDLE group, seat ${ref.index + 1}`;
  return 'the end of the BOTTOM group';
}

/** The running order the board currently describes, holes removed. */
function boardOrder(board: Board): string[] {
  const out: string[] = [];
  if (board.pole) out.push(board.pole);
  for (const id of board.top) if (id) out.push(id);
  for (const id of board.middle) if (id) out.push(id);
  out.push(...board.bottom);
  return out;
}

// ---------------------------------------------------------------------------
// Placement
// ---------------------------------------------------------------------------

type Destination = SeatRef | { group: 'bubble'; top: boolean } | { group: 'dnq' };

interface PlacementOutcome {
  destination: Destination;
  /** The seat the printed rule asked for, before any "already full" fallback. */
  intended: string;
  where: string;
  fellBack: boolean;
}

function trophyIsPresent(trophy: Trophy): boolean {
  return trophy !== null;
}

/**
 * RWEG/p.3 — the four PLACEMENT readings, verbatim:
 *
 *   A: "GOLD Trophy driver gains POLE or [blue die] position in TOP group;
 *       SILVER or BRONZE trophy driver gains [blue die] position in TOP group;
 *       NON trophy driver gains [blue die] position in MIDDLE group."
 *   B: "Trophy driver gains [blue die] position in TOP group; NON trophy driver
 *       gains next position in BOTTOM group."
 *   C: "Trophy driver gains [blue die] position in MIDDLE group, NON trophy
 *       driver placed at TOP of 'bubble' stack."
 *   D: "Trophy driver placed in 'bubble' stack; NON trophy driver fails to
 *       qualify."
 *
 * RULING (D-RW-4): PLACEMENT A's GOLD reading is "POLE **or** [blue die]
 * position in TOP" — a tabletop choice. A gold-trophy driver on the best
 * reading the chart can give takes the POLE when it is free, and the blue box
 * otherwise. Nobody hands back the pole.
 */
/**
 * Take a seat that may already be occupied, moving its occupant on.
 *
 * The ONLY placement in Race Week that displaces somebody — every other one
 * falls back around an occupied seat rather than through it. It exists for
 * EZRA RC's gold branch (D-EZ-25) and nothing else, which is why it is written
 * here rather than folded into `takeExact`: a helper that displaces by default
 * would be a foot-gun for every other rule in this file.
 *
 * The displaced driver goes to the first free seat from his own group down —
 * the same fallback `takeExact` uses — so nobody is lost and the board stays a
 * board.
 */
function takeExactDisplacing(
  board: Board,
  ref: SeatRef,
  intended: string,
  bottomCap: number,
): PlacementOutcome {
  if (ref.group !== 'top') throw new Error('displacing placement is only defined for a TOP seat');
  const sitting = board.top[ref.index];
  if (sitting === null) {
    return { destination: ref, intended, where: describeSeat(ref), fellBack: false };
  }
  // Free the seat, then re-place the man who was in it.
  board.top[ref.index] = null;
  const moved = firstFreeSeatFrom(board, 'top', bottomCap);
  applyDestination(board, moved ?? { group: 'bubble', top: false }, sitting);
  return {
    destination: ref,
    intended,
    where: `${describeSeat(ref)} — ${sitting} moves back`,
    fellBack: false,
  };
}

export function resolvePlacement(
  board: Board,
  letter: PlacementLetter,
  trophy: Trophy,
  blue: number,
  bottomCap: number,
  rc: { rcSecondOnGrid: boolean } = { rcSecondOnGrid: false },
): PlacementOutcome {
  const hasTrophy = trophyIsPresent(trophy);

  const takeExact = (ref: SeatRef, intended: string, from: SeatGroup): PlacementOutcome => {
    const free =
      ref.group === 'pole'
        ? board.pole === null
        : ref.group === 'top'
          ? board.top[ref.index] === null
          : ref.group === 'middle'
            ? board.middle[ref.index] === null
            : board.bottom.length < bottomCap;
    if (free) return { destination: ref, intended, where: describeSeat(ref), fellBack: false };
    const fallback = firstFreeSeatFrom(board, from, bottomCap);
    if (!fallback) {
      return { destination: { group: 'bubble', top: false }, intended, where: 'the bubble stack', fellBack: true };
    }
    return { destination: fallback, intended, where: describeSeat(fallback), fellBack: true };
  };

  if (letter === 'A') {
    if (trophy === 'gold') {
      if (board.pole === null) {
        return { destination: { group: 'pole' }, intended: 'the POLE', where: 'the POLE', fellBack: false };
      }
      // EZRA RC's gold branch — "if the Pole is not open the driver will be
      // placed second on the grid". Second on the grid IS TOP box 1.
      //
      // D-EZ-25: the printed sentence goes on, "any previously placed drivers
      // superseded by this placement will automatically move back one
      // position" — an INSERT-AND-CASCADE. Race Week's board is fixed seats and
      // has never inserted-and-shuffled; rebuilding it for this would change the
      // model STEP SIX depends on. So he TAKES the seat and the one driver he
      // displaces falls to the first free seat, which is the fallback every
      // other placement in this file already uses.
      //
      // Measured at ZERO occurrences in 40 seasons (120 road-course races, 240
      // RC driver-races), so the difference between this and the literal
      // cascade is not something a career will ever show.
      if (rc.rcSecondOnGrid) {
        return takeExactDisplacing(board, { group: 'top', index: 0 }, 'second on the grid (RC)', bottomCap);
      }
      return takeExact({ group: 'top', index: blue - 1 }, `TOP box ${blue}`, 'top');
    }
    if (hasTrophy) return takeExact({ group: 'top', index: blue - 1 }, `TOP box ${blue}`, 'top');
    return takeMiddleBox(board, blue, bottomCap);
  }

  if (letter === 'B') {
    if (hasTrophy) return takeExact({ group: 'top', index: blue - 1 }, `TOP box ${blue}`, 'top');
    return takeExact({ group: 'bottom' }, 'the next position in the BOTTOM group', 'bottom');
  }

  if (letter === 'C') {
    if (hasTrophy) return takeMiddleBox(board, blue, bottomCap);
    return {
      destination: { group: 'bubble', top: true },
      intended: 'the top of the bubble stack',
      where: 'the top of the bubble stack',
      fellBack: false,
    };
  }

  // PLACEMENT D
  if (hasTrophy) {
    return {
      destination: { group: 'bubble', top: false },
      intended: 'the bubble stack',
      where: 'the bubble stack',
      fellBack: false,
    };
  }
  return { destination: { group: 'dnq' }, intended: 'failed to qualify', where: 'FAILED TO QUALIFY', fellBack: false };
}

/** The blue die picks a MIDDLE *box*; either of its two seats will do. */
function takeMiddleBox(board: Board, blue: number, bottomCap: number): PlacementOutcome {
  const intended = `MIDDLE box ${blue}`;
  for (const index of middleBoxSeats(blue)) {
    if (board.middle[index] === null) {
      const ref: SeatRef = { group: 'middle', index };
      return { destination: ref, intended, where: describeSeat(ref), fellBack: false };
    }
  }
  const fallback = firstFreeSeatFrom(board, 'middle', bottomCap);
  if (!fallback) {
    return { destination: { group: 'bubble', top: false }, intended, where: 'the bubble stack', fellBack: true };
  }
  return { destination: fallback, intended, where: describeSeat(fallback), fellBack: true };
}

/**
 * RWEG/p.4 — "If a driver earns a performance chip from his TV LIST dice roll,
 * but his PLACEMENT READING calls for him to be placed in the BOTTOM group or
 * 'bubble' stack, he forfiets his performance chip."
 *
 * The trigger is the READING, not where he finally lines up. A driver sent to
 * the BOTTOM group loses the chip there and then; if the p.4 open-spaces
 * clarification later moves him up into the MIDDLE group he does not get it
 * back, because it was already forfeited.
 */
function forfeitBannedChips(state: WeekState, driverId: string, destination: Destination, cite: string): void {
  if (destination.group === 'pole' || destination.group === 'top' || destination.group === 'middle') return;
  const held = state.chips.get(driverId) ?? 0;
  if (held <= 0) return;
  const where =
    destination.group === 'bottom'
      ? 'placed in the BOTTOM group'
      : destination.group === 'bubble'
        ? 'placed on the bubble stack'
        : 'failed to qualify';
  state.chips.set(driverId, 0);
  state.chipsForfeited.push({ driverId, chips: held, reason: where, ruleCitation: `${cite} + ${CITE_CHIP_BAN}` });
  push(state, {
    eventType: 'race-week',
    momentTag: 'raceweek.chip-forfeit',
    ruleCitation: `${cite} + ${CITE_CHIP_BAN}`,
    actorIds: [driverId],
    positionDeltas: [],
    modifiers: [{ name: 'chips-forfeited', value: -held, note: where }],
    dice: [],
    description: `${nameOf(state, driverId)} forfeits ${held} performance chip${held === 1 ? '' : 's'} — ${where}.`,
  });
}

function applyDestination(board: Board, destination: Destination, driverId: string): void {
  if (destination.group === 'bubble') {
    if (destination.top) board.bubble.unshift(driverId);
    else board.bubble.push(driverId);
    return;
  }
  if (destination.group === 'dnq') {
    board.dnq.push(driverId);
    return;
  }
  seat(board, destination, driverId);
}

// ---------------------------------------------------------------------------
// Steps three, four and five: trophy tables, no die roll
// ---------------------------------------------------------------------------

/** RWEG/p.3 STEP THREE, RWEG/p.3 STEP FOUR, RWEG/p.4 STEP FIVE. */
function placeByTrophy(
  board: Board,
  step: 'fast' | 'slow' | 'uneventful',
  trophy: Trophy,
  bottomCap: number,
): PlacementOutcome {
  const highestIn = (group: 'top' | 'middle'): PlacementOutcome => {
    const ref = firstFreeSeatFrom(board, group, bottomCap);
    const intended = `the highest available position in the ${group.toUpperCase()} group`;
    if (!ref) {
      return { destination: { group: 'bubble', top: false }, intended, where: 'the bubble stack', fellBack: true };
    }
    return { destination: ref, intended, where: describeSeat(ref), fellBack: ref.group !== group };
  };
  const nextInBottom = (): PlacementOutcome => {
    const intended = 'the next position in the BOTTOM group';
    if (board.bottom.length < bottomCap) {
      return { destination: { group: 'bottom' }, intended, where: describeSeat({ group: 'bottom' }), fellBack: false };
    }
    return { destination: { group: 'bubble', top: false }, intended, where: 'the bubble stack', fellBack: true };
  };
  const nextInBubble = (): PlacementOutcome => ({
    destination: { group: 'bubble', top: false },
    intended: 'the next position in the bubble stack',
    where: 'the bubble stack',
    fellBack: false,
  });

  if (step === 'fast') {
    // GOLD: "POLE position, or highest available position in TOP group."
    if (trophy === 'gold') {
      if (board.pole === null) {
        return { destination: { group: 'pole' }, intended: 'the POLE', where: 'the POLE', fellBack: false };
      }
      return highestIn('top');
    }
    if (trophy === 'silver') return highestIn('top');
    if (trophy === 'bronze') return highestIn('middle');
    return nextInBottom();
  }

  if (step === 'slow') {
    // GOLD: "Next position in BOTTOM group." SILVER/BRONZE: bubble. NONE: DNQ.
    if (trophy === 'gold') return nextInBottom();
    if (trophy === 'silver' || trophy === 'bronze') return nextInBubble();
    return { destination: { group: 'dnq' }, intended: 'failed to qualify', where: 'FAILED TO QUALIFY', fellBack: false };
  }

  // STEP FIVE — UNEVENTFUL.
  if (trophy === 'gold') return highestIn('top');
  if (trophy === 'silver') return highestIn('middle');
  if (trophy === 'bronze') return nextInBottom();
  return nextInBubble();
}

// ---------------------------------------------------------------------------
// The engine
// ---------------------------------------------------------------------------

interface WeekState {
  board: Board;
  bottomCap: number;
  fieldSize: number;
  dice: Dice;
  events: RuleEvent[];
  byId: Map<string, DriverCard>;
  placed: Set<string>;
  chips: Map<string, number>;
  chipWiped: Set<string>;
  unavailable: Map<string, UnableToRace>;
  fines: Fine[];
  pointsPenalties: PointsPenalty[];
  suspensions: Suspension[];
  injuries: Injury[];
  teamTransfers: TeamTransfer[];
  feuds: FeudEntry[];
  feudIds: Set<string>;
  fumeCandidates: Set<string>;
  placements: Map<string, PlacementLetter>;
  categories: Map<string, WeekCategory>;
  interviews: Interview[];
  tvIds: string[];
  carryOverIds: string[];
  tvResults: TvListResult[];
  goldOverrideId: string | null;
  /** EZRA RC — is this week's race at a road course? */
  roadCourse: boolean;
  badWeather: boolean;
  chipsForfeited: ChipForfeit[];
}

/**
 * Run a complete RACE WEEK. Same roster, same seed, same week — every roll,
 * every fine, every feud, in the same order.
 */
export function runRaceWeek(input: RaceWeekInput): RaceWeekResult {
  const options = input.options ?? {};
  const fieldSize = options.fieldSize ?? MAX_STARTING_FIELD;
  if (!Number.isInteger(fieldSize) || fieldSize < 1) {
    throw new RangeError(`the starting field must be a positive whole number, got ${fieldSize}`);
  }
  if (fieldSize > MAX_STARTING_FIELD) {
    throw new RangeError(
      `RWEG/p.4 rounds the field out to ${MAX_STARTING_FIELD}; ${fieldSize} is more than the boards hold`,
    );
  }
  // The LEADER space, six TOP boxes and twelve MIDDLE seats are printed on the
  // boards and cannot be shrunk; a field smaller than those nineteen has
  // nowhere for a PLACEMENT A reading to put anybody.
  const MIN_FIELD = 1 + TOP_SEATS + MIDDLE_SEATS;
  if (fieldSize < MIN_FIELD) {
    throw new RangeError(
      `Boards A and B seat ${MIN_FIELD} cars before the BOTTOM group starts; a ${fieldSize}-car field cannot be laid out`,
    );
  }
  const seen = new Set<string>();
  for (const card of input.roster) {
    if (seen.has(card.driverId)) throw new Error(`duplicate driver on the roster: ${card.driverId}`);
    seen.add(card.driverId);
  }

  const bottomCap = Math.max(0, fieldSize - 1 - TOP_SEATS - MIDDLE_SEATS);
  const state: WeekState = {
    board: newBoard(),
    bottomCap,
    fieldSize,
    dice: input.dice ?? new Dice(input.seed),
    events: [],
    byId: new Map(input.roster.map((c) => [c.driverId, c])),
    placed: new Set(),
    chips: new Map(),
    chipWiped: new Set(),
    unavailable: new Map(),
    fines: [],
    pointsPenalties: [],
    suspensions: [],
    injuries: [],
    teamTransfers: [],
    feuds: (options.carryOverFeuds ?? []).map((f) => ({ ...f })),
    feudIds: new Set((options.carryOverFeuds ?? []).flatMap((f) => [f.a, f.b])),
    fumeCandidates: new Set(),
    placements: new Map(),
    categories: new Map(),
    interviews: [],
    tvIds: [],
    carryOverIds: [],
    tvResults: [],
    goldOverrideId: null,
    roadCourse: options.roadCourse === true,
    badWeather: false,
    chipsForfeited: [],
  };

  // ---- STEP ONE ---------------------------------------------------------
  // RWEG/p.2: "Shuffle all the driver cards thoroughly." The shuffle runs off
  // its own stream, derived from the same seed, so that adding a driver to the
  // roster cannot move a single chart roll for the drivers already on it.
  const shuffleRng = makeRng((input.seed ^ 0x517cc1b7) >>> 0);
  const stack = input.roster.map((c) => c.driverId);
  for (let i = stack.length - 1; i > 0; i--) {
    const j = Math.floor(shuffleRng() * (i + 1));
    [stack[i], stack[j]] = [stack[j], stack[i]];
  }
  const rosterOrder = [...stack];

  // Drivers already unavailable from a previous week never enter the deck.
  for (const id of options.unavailableIds ?? []) {
    if (!state.byId.has(id)) continue;
    state.unavailable.set(id, {
      driverId: id,
      reason: 'carried-over',
      text: 'unavailable before the week began (a suspension or injury still running)',
      chart: null,
      code: null,
      ruleCitation: CITE_SETUP,
    });
  }

  push(state, {
    eventType: 'race-week',
    momentTag: 'raceweek.setup',
    ruleCitation: CITE_SETUP,
    actorIds: [],
    positionDeltas: [],
    modifiers: [
      { name: 'licensed-roster', value: input.roster.length, note: 'drivers holding a licence this week' },
      { name: 'starting-field', value: fieldSize, note: 'RWEG/p.4 STEP SIX rounds the field out to this' },
      { name: 'unavailable-before-the-week', value: state.unavailable.size },
    ],
    dice: [],
    description:
      `Race week opens with ${input.roster.length} licensed drivers and ${fieldSize} starting spots. ` +
      'The cards are shuffled.',
  });

  // ---- STEP TWO ---------------------------------------------------------
  //
  // D-018 — the phase announcements below exist for ONE reader: a player
  // hand-rolling the weekend. The prompt can name the die but not the driver
  // (the dice source is several frames below the driver), so the phase is the
  // only thing that tells them WHICH of the week's ~50 draws they are answering.
  // Absent, every prompt of the week reads "roll a d6" and nothing else.
  // Costless when the week is seeded: nothing ever reads it.
  state.dice.phase = 'race week — TV LIST (RWEG/p.2 STEP TWO)';
  const cursor = { i: 0 };
  runTvListStep(state, stack, cursor, options);

  // ---- Bad weather short-circuit ----------------------------------------
  // TV EVENT Z 21/41/61 — "Bad weather cancels qualifying; ALL drivers,
  // PLACEMENT C." There is nothing left to qualify for, so STEPS THREE, FOUR
  // and FIVE do not happen: everybody who can still race takes PLACEMENT C.
  // Everything the week already generated — fines, injuries, suspensions,
  // feuds — stands; those happened during the week, not during qualifying.
  if (state.badWeather) {
    state.dice.phase = 'race week — BAD WEATHER, qualifying cancelled (TV EVENT Z 21/41/61)';
    applyBadWeather(state, rosterOrder);
  } else {
    state.dice.phase = 'race week — FAST WEEK qualifying (RWEG/p.3 STEP THREE)';
    runDealStep(state, stack, cursor, 'fast', CITE_FAST_WEEK, 'FAST WEEK');
    state.dice.phase = 'race week — SLOW WEEK qualifying (RWEG/p.3 STEP FOUR)';
    runDealStep(state, stack, cursor, 'slow', CITE_SLOW_WEEK, 'SLOW WEEK');
    state.dice.phase = 'race week — UNEVENTFUL WEEK (RWEG/p.4 STEP FIVE)';
    runUneventfulStep(state, stack, cursor);
  }

  // ---- Clarification: open spaces, then STEP SIX -------------------------
  // RWEG prints STEP SIX before the clarifications, but the two have to run in
  // this order to mean anything. STEP SIX counts the field and rounds it out to
  // 43 "at the end of the BOTTOM group" — and the BOTTOM group only holds 24.
  // If a hole is left in TOP or MIDDLE while BOTTOM is already full, counting
  // first would stall the field one car short of 43 with drivers still on the
  // bubble. Closing the holes first (the p.4 clarification) frees the seat that
  // STEP SIX then fills off the bubble. Nothing is promoted that STEP SIX put
  // there, because STEP SIX has not run yet.
  compactOpenSpaces(state);
  const bubbleBefore = [...state.board.bubble];
  roundOutTheField(state);
  // A second pass, for the case where the roster was too small to fill the
  // front of the field before the bubble was called on. A no-op otherwise.
  compactOpenSpaces(state);

  // ---- Chips: the BOTTOM/bubble ban -------------------------------------
  const grid = boardOrder(state.board);
  enforceChipBan(state, grid);

  // ---- FUME list --------------------------------------------------------
  const onGrid = new Set(grid);
  const fuming = [...state.fumeCandidates].filter((id) => onGrid.has(id) && !state.feudIds.has(id));
  if (fuming.length > 0) {
    push(state, {
      eventType: 'race-week',
      momentTag: 'raceweek.fume-list',
      ruleCitation: CITE_FUME,
      actorIds: fuming,
      positionDeltas: [],
      modifiers: fuming.map((id) => ({
        name: 'fuming',
        value: nameOf(state, id),
        note: 'his week ended at PLACEMENT D',
      })),
      dice: [],
      description:
        `${fuming.length} driver${fuming.length === 1 ? '' : 's'} take${fuming.length === 1 ? 's' : ''} ` +
        `the green flag fuming: ${fuming.map((id) => nameOf(state, id)).join(', ')}.`,
    });
  }

  // ---- The grid ---------------------------------------------------------
  const gridDeltas: PositionDelta[] = grid.map((id, i) => ({
    driverId: id,
    from: 0,
    to: i + 1,
    note: i === 0 ? 'pole' : undefined,
  }));
  push(state, {
    eventType: 'race-week',
    momentTag: 'raceweek.grid',
    ruleCitation: CITE_ROUND_OUT,
    actorIds: grid,
    positionDeltas: gridDeltas,
    modifiers: [
      { name: 'field-size', value: grid.length },
      { name: 'pole-sitter', value: grid.length > 0 ? nameOf(state, grid[0]) : 'nobody' },
      { name: 'did-not-qualify', value: state.board.dnq.length },
      { name: 'unable-to-race', value: state.unavailable.size },
    ],
    dice: [],
    description:
      grid.length > 0
        ? `${nameOf(state, grid[0])} leads the field to the green with ${grid.length} cars starting.`
        : 'No car qualified for this race.',
  });

  const bubbleUnused = state.board.bubble.filter((id) => !grid.includes(id));

  const tvQualityFor = options.tvQualityFor ?? defaultTvQuality;
  const tvList: Record<string, Quality> = {};
  for (const id of state.tvIds) {
    const card = state.byId.get(id);
    if (card && onGrid.has(id)) tvList[id] = tvQualityFor(card);
  }

  const chips: Record<string, number> = {};
  for (const [id, n] of state.chips) if (n > 0 && onGrid.has(id)) chips[id] = n;

  return {
    seed: input.seed,
    // Null unless the week was fed — see the note on the field.
    diceStream: state.dice.isFed ? state.dice.stream : null,
    rosterOrder,
    grid: grid.map((id) => state.byId.get(id)!),
    tvList,
    tvDriverIds: [...state.tvIds],
    carryOverTvIds: [...state.carryOverIds],
    tvResults: state.tvResults,
    interviews: state.interviews,
    feuds: state.feuds,
    fuming,
    categories: Object.fromEntries(state.categories),
    placements: Object.fromEntries(state.placements),
    dnq: [...state.board.dnq],
    bubble: bubbleBefore,
    bubbleUnused,
    unableToRace: [...state.unavailable.values()],
    fines: state.fines,
    pointsPenalties: state.pointsPenalties,
    suspensions: state.suspensions,
    injuries: state.injuries,
    teamTransfers: state.teamTransfers,
    chips,
    chipsForfeited: state.chipsForfeited,
    badWeather: state.badWeather,
    events: state.events,
  };
}

/**
 * RULING (D-RW-2). `Car.tvQuality` in this engine is typed as a `Quality`, but
 * the rulebook (p.iii) calls TV "the temporary TV quality" — a seventh quality
 * of its own, not a bonus copy of one of the six. Granting a driver a quality
 * he does not already hold would silently hand every TV driver an extra rating
 * the booklet never gives him.
 *
 * So the TV card is stamped with a quality the driver ALREADY has. `types.ts`
 * `carQualities` then adds nothing, while `hasQuality(car, 'TV')` — which only
 * asks whether `tvQuality` is set — answers the RACE CAM's TV call correctly.
 * Being on the TV list grants the TV flag and nothing else.
 */
function defaultTvQuality(card: DriverCard): Quality {
  return card.qualities[0] ?? 'STAR';
}

// ---------------------------------------------------------------------------
// STEP TWO — the TV LIST
// ---------------------------------------------------------------------------

function runTvListStep(
  state: WeekState,
  stack: string[],
  cursor: { i: number },
  options: RaceWeekOptions,
): void {
  // RWEG/p.2: "Roll the two metallic dice, add them, and randomly select that
  // many driver cards for the TV LIST."
  state.dice.phase = 'race week — how many drivers make the TV LIST (RWEG/p.2 STEP TWO)';
  const roll = state.dice.sum2d6();
  const countRecord: DiceRecord = {
    kind: '2d6-sum',
    dice: roll.dice,
    total: roll.total,
    label: 'TV LIST count (metallic dice)',
    flags: { doubles: roll.doubles },
  };

  // RWEG/p.3 — carry-overs join the list without a roll and are ADDITIVE to
  // the 2d6 count (cross_reference §A21: the owner's own Quick Ref says "Add
  // prev. week's winner").
  //
  // AMBIGUITY (D-RW-5): "without rolling on the TV LIST charts" is read
  // literally — a carry-over is a TV driver (gold TV card, TV quality) but has
  // no TV LIST result, so he stays in the deck and is placed by whichever of
  // STEPS THREE/FOUR/FIVE deals him. The alternative reading — that he is
  // dealt a TV LIST roll anyway — would make the phrase mean nothing.
  const carryOvers = (options.carryOverTvIds ?? []).filter(
    (id) => state.byId.has(id) && !state.unavailable.has(id),
  );
  for (const id of carryOvers) {
    if (state.tvIds.includes(id)) continue;
    state.tvIds.push(id);
    state.carryOverIds.push(id);
    push(state, {
      eventType: 'race-week',
      momentTag: 'raceweek.tv-list.carry-over',
      ruleCitation: CITE_CARRYOVER,
      actorIds: [id],
      positionDeltas: [],
      modifiers: [{ name: 'carry-over-tv', value: nameOf(state, id), note: 'no TV LIST roll required' }],
      dice: [],
      description: `${nameOf(state, id)} is still on television from last week and makes the TV LIST without a roll.`,
    });
  }

  const drawn: string[] = [];
  for (let n = 0; n < roll.total && cursor.i < stack.length; n++) {
    const id = stack[cursor.i++];
    if (state.unavailable.has(id)) {
      n--; // an unavailable card is not a TV driver; deal the next one instead
      continue;
    }
    drawn.push(id);
    if (!state.tvIds.includes(id)) state.tvIds.push(id);
    state.categories.set(id, 'tv');
  }

  push(state, {
    eventType: 'race-week',
    momentTag: 'raceweek.tv-list.count',
    ruleCitation: CITE_TV_LIST,
    actorIds: drawn,
    positionDeltas: [],
    modifiers: [
      { name: 'rolled-tv-drivers', value: drawn.length },
      { name: 'carry-over-tv-drivers', value: carryOvers.length },
    ],
    dice: [countRecord],
    description:
      `The metallic dice come up ${roll.dice[0]} and ${roll.dice[1]} — ${roll.total} drivers on this week's TV LIST` +
      (carryOvers.length > 0 ? `, plus ${carryOvers.length} carried over from last week.` : '.'),
  });

  // RWEG/p.2 — "the FIRST driver on the TV List is always considered to have a
  // GOLD TROPHY symbol, whether he has one or not."
  //
  // RULING (D-RW-6): the rule sits inside STEP TWO's description of the deal,
  // so it attaches to the first card DEALT onto the list, not to a carry-over
  // who was never dealt.
  if (drawn.length > 0) {
    state.goldOverrideId = drawn[0];
    push(state, {
      eventType: 'race-week',
      momentTag: 'raceweek.tv-list.gold',
      ruleCitation: CITE_TV_GOLD,
      actorIds: [drawn[0]],
      positionDeltas: [],
      modifiers: [
        { name: 'printed-trophy', value: state.byId.get(drawn[0])!.trophy ?? 'none' },
        { name: 'trophy-this-week', value: 'gold', note: 'first driver on the TV LIST' },
      ],
      dice: [],
      description: `${nameOf(state, drawn[0])} is first on the TV LIST and counts as a GOLD TROPHY driver this week.`,
    });
  }

  // Process the list IN ORDER — an altercation reaches for the next card, so
  // order is load-bearing (RWEG/p.6 rows 31 and 42).
  const resolved = new Set<string>();
  for (let k = 0; k < drawn.length; k++) {
    const id = drawn[k];
    if (resolved.has(id)) continue; // pulled in as somebody's altercation partner
    // D-018 — AND THIS ONE NAMES THE DRIVER, which the race's prompts cannot.
    //
    // Measured: the TV LIST is 390 of a race week's 438 draws over twelve
    // weeks, so without this a player hand-rolling the weekend reads the same
    // sentence thirty times and has no idea whose week he is deciding. Here the
    // driver is in scope — `runTvListStep` is dealing him — so naming him costs
    // nothing and is not a guess. (In the RACE the driver is eight frames above
    // the dice and a name printed there would be invented; see the D-018 ruling.)
    state.dice.phase =
      `race week — TV LIST, ${nameOf(state, id)} (${k + 1} of ${drawn.length}) (RWEG/p.5-6)`;
    resolveTvDriver(state, id, {
      list: drawn,
      index: k,
      resolved,
      stack,
      cursor,
      options,
    });
  }
}

interface TvContext {
  list: string[];
  index: number;
  resolved: Set<string>;
  stack: string[];
  cursor: { i: number };
  options: RaceWeekOptions;
}

/**
 * EZRA-S2 §4.0 RC — "Road Course Expert gets an automatic Trophy upgrade of 1
 * level when qualifying on a Road Course."
 *
 * D-EZ-24: **"no trophy" is a rung**, so the ladder is none → bronze → silver →
 * gold and every RC driver benefits, not only the 51% who already hold one.
 * Race Week's own PLACEMENT readings split on trophy-versus-non-trophy, so
 * crossing that line is exactly the kind of upgrade the rule is for; a reading
 * that did nothing for half the drivers who could hold the trait would make it
 * the weakest of the eight by a distance.
 *
 * Measured: it fires 5.3 times a season.
 */
export const TROPHY_LADDER: readonly Trophy[] = [null, 'bronze', 'silver', 'gold'];

export function upgradeTrophy(trophy: Trophy): Trophy {
  const i = TROPHY_LADDER.indexOf(trophy);
  return TROPHY_LADDER[Math.min(i + 1, TROPHY_LADDER.length - 1)];
}

/**
 * The effective trophy for placement — gold if he is first on the TV LIST, and
 * one rung up if he is a Road Course Expert qualifying on a road course.
 *
 * THIS IS A QUALIFYING-TIME EFFECT AND NEVER TOUCHES THE CARD. Same discipline
 * as D-EZ-15's interview effects: the roster card is not written to, so the
 * upgrade cannot leak into the next race or into the winter re-rate.
 */
function trophyOf(state: WeekState, driverId: string): Trophy {
  if (state.goldOverrideId === driverId) return 'gold';
  const printed = state.byId.get(driverId)?.trophy ?? null;
  if (state.roadCourse && state.byId.get(driverId)?.trait === 'RC') return upgradeTrophy(printed);
  return printed;
}

/**
 * Was he ALREADY gold before the upgrade?
 *
 * The gold branch says "If the driver **already has** a Gold Trophy" — so a
 * silver RC driver promoted to gold by the first sentence does NOT qualify for
 * the second. The distinction is the whole reason the two sentences exist: the
 * branch is the consolation for a driver the upgrade cannot help.
 */
function rcAlreadyGold(state: WeekState, driverId: string): boolean {
  if (!state.roadCourse) return false;
  const card = state.byId.get(driverId);
  return card?.trait === 'RC' && card.trophy === 'gold';
}

function resolveTvDriver(state: WeekState, driverId: string, ctx: TvContext): void {
  ctx.resolved.add(driverId);

  // Roll the chart chain. Each throw is red-white-blue together (RWEG/p.2);
  // the blue that matters is the one on the throw that produced the final
  // reading, since that is the throw whose PLACEMENT is being read.
  const chain: ChartReading[] = [];
  const diceRecords: DiceRecord[] = [];
  let chart: ChartId = 'race-week-tv-list';
  let entry: RaceWeekChartEntry;
  let code: string;
  let blue = 0;
  const allowDeath = ctx.options.allowDriverDeath ?? true;

  for (let guard = 0; ; guard++) {
    if (guard > 32) throw new Error('race week chart chain did not terminate');
    const read = state.dice.code1166();
    blue = state.dice.d6();
    code = read.code;
    entry = chartEntry(chart, code);
    chain.push({ chart, code, text: entry.text });
    diceRecords.push({
      kind: '11-66',
      dice: [read.red, read.white],
      code: read.code,
      label: `${CHARTS[chart].name} (red + white)`,
      faces: { red: read.red, white: read.white },
      flags: { doubles: read.doubles },
    });
    diceRecords.push({
      kind: 'd6',
      dice: [blue],
      label: 'blue die (PLACEMENT box)',
      faces: { blue },
    });

    // TV EVENT Z 13, suppressed by configuration: re-read the same chart. The
    // booklet has no "nothing happens" row, and every other Z reading is a
    // legitimate season-level event, so re-rolling is the least invented fix.
    const dies = entry.effects.some((e) => e.type === 'driverDies');
    if (dies && !allowDeath) {
      push(state, {
        eventType: 'race-week',
        momentTag: 'raceweek.tv-event-z.death-suppressed',
        ruleCitation: citeRow(chart, code),
        actorIds: [driverId],
        positionDeltas: [],
        modifiers: [{ name: 'allowDriverDeath', value: 'false', note: 'configuration suppressed the reading' }],
        dice: [],
        description: `TV EVENT Z ${code} is suppressed by league policy; the reading is taken again.`,
      });
      continue;
    }

    const reroll = entry.effects.find((e) => e.type === 'rerollOn');
    if (!reroll) break;
    chart = reroll.chart as ChartId;
  }

  const final = { chart, code, entry };

  // TV INTERVIEWS are quotable dialogue: keep them for the broadcast booth.
  if (final.chart === 'tv-interviews' && final.entry.placement) {
    state.interviews.push({
      driverId,
      code: final.code,
      text: final.entry.text,
      tokens: tokensIn(final.entry.text),
      placement: final.entry.placement,
    });
  }

  applyTvResult(state, driverId, final.chart, final.code, final.entry, blue, diceRecords, chain, ctx, false);
}

/**
 * Apply one finished chart reading to one driver: its effects first (they can
 * take him out of the race entirely), then its PLACEMENT.
 */
function applyTvResult(
  state: WeekState,
  driverId: string,
  chart: ChartId,
  code: string,
  entry: RaceWeekChartEntry,
  blue: number,
  diceRecords: DiceRecord[],
  chain: ChartReading[],
  ctx: TvContext,
  drawnAsPartner: boolean,
): void {
  const cite = citeRow(chart, code);
  const modifiers: Modifier[] = [
    { name: 'chart', value: CHARTS[chart].name },
    { name: 'code', value: code },
  ];

  let placement = entry.placement;
  let partnerId: string | null = null;
  let outOfTheRace = false;

  for (const effect of entry.effects) {
    switch (effect.type) {
      case 'trophyPlacement': {
        // "(GOLD driver PLACEMENT A)" and "(GOLD or SILVER driver PLACEMENT A)"
        const trophies = effect.trophies as string[];
        const mine = trophyOf(state, driverId);
        if (mine !== null && trophies.includes(mine)) {
          placement = effect.placement as PlacementLetter;
          modifiers.push({
            name: 'trophy-upgrade',
            value: placement,
            note: `${mine.toUpperCase()} trophy takes the parenthetical reading`,
          });
        }
        break;
      }
      case 'chip': {
        const n = effect.count as number;
        state.chips.set(driverId, (state.chips.get(driverId) ?? 0) + n);
        modifiers.push({ name: 'performance-chips', value: n, note: 'earned during race week' });
        break;
      }
      case 'chipWipe': {
        // AMBIGUITY (D-RW-7): "disregard all performance chips earned this
        // race" — whose? The row is about THIS driver's crew, so it is read as
        // his own chips. A field-wide wipe from one team's pit trouble would be
        // absurd. He is also flagged so a chip earned later in the same reading
        // cannot sneak back in.
        const had = state.chips.get(driverId) ?? 0;
        if (had > 0) {
          state.chipsForfeited.push({
            driverId,
            chips: had,
            reason: 'chips earned this race are disregarded',
            ruleCitation: cite,
          });
        }
        state.chips.set(driverId, 0);
        state.chipWiped.add(driverId);
        modifiers.push({ name: 'chip-wipe', value: -had, note: 'all chips earned this race disregarded' });
        break;
      }
      case 'fine': {
        recordFine(state, driverId, effect, chart, code);
        modifiers.push({
          name: 'fine',
          value: (effect.amount as number | null) ?? 'amount not printed',
          note: effect.offense as string,
        });
        break;
      }
      case 'lossOfPoints': {
        // RULING (D-RW-8). "[and loss of points]" — the base game has no
        // championship points at all (cross_reference §B2), so no printed
        // number can be deducted. The ruling is that he forfeits the points he
        // EARNS IN THIS RACE, which is a penalty every points system can apply
        // without one being invented for it.
        state.pointsPenalties.push({ driverId, scope: 'this-race', chart, code, ruleCitation: cite });
        modifiers.push({
          name: 'points-penalty',
          value: 'this race',
          note: 'forfeits the championship points earned in this race',
        });
        break;
      }
      case 'suspend': {
        const races = rollDiceSpec(state, ctx.options.diceRaces ?? DICE_RACES, diceRecords, 'suspension length');
        state.suspensions.push({ driverId, races, includesThisRace: true, chart, code, ruleCitation: cite });
        markUnavailable(state, driverId, 'suspended', entry.text, chart, code, cite);
        outOfTheRace = true;
        modifiers.push({ name: 'suspension', value: races, note: 'races, starting with this one' });
        break;
      }
      case 'cannotRace': {
        const reason = effect.reason as UnavailableReason;
        if (reason === 'injury') {
          state.injuries.push({ driverId, duration: 'this-race', chart, code, ruleCitation: cite });
        }
        markUnavailable(state, driverId, reason, entry.text, chart, code, cite);
        outOfTheRace = true;
        modifiers.push({ name: 'cannot-race', value: reason });
        break;
      }
      case 'teamWithdraws': {
        // Sagliano p.12: "A Team can have only one Driver." So the team that
        // withdraws is this entry, not a stablemate's.
        const record = state.unavailable.get(driverId);
        if (record) record.teamWithdrew = true;
        modifiers.push({ name: 'team-withdrew', value: 'yes' });
        break;
      }
      case 'seasonEndingInjury': {
        state.injuries.push({ driverId, duration: 'season', chart, code, ruleCitation: cite });
        markUnavailable(state, driverId, 'season-injury', entry.text, chart, code, cite);
        outOfTheRace = true;
        modifiers.push({ name: 'injury', value: 'rest of season' });
        break;
      }
      case 'driverDies': {
        markUnavailable(state, driverId, 'dead', entry.text, chart, code, cite);
        outOfTheRace = true;
        modifiers.push({ name: 'fatality', value: nameOf(state, driverId) });
        break;
      }
      case 'quitsTeam': {
        const weeks = rollDiceSpec(state, ctx.options.diceRaces ?? DICE_RACES, diceRecords, 'weeks before he signs');
        state.teamTransfers.push({
          driverId,
          weeks,
          fromTeamId: state.byId.get(driverId)?.teamId,
          chart,
          code,
          ruleCitation: cite,
        });
        // AMBIGUITY (D-RW-9): the row prints no PLACEMENT. A driver who has
        // just quit his team has no car this week, so he does not race; the
        // transfer completes `weeks` weeks later. The alternative — that he
        // races for the team he has walked out on — cannot be squared with the
        // printed sentence.
        markUnavailable(state, driverId, 'quit', entry.text, chart, code, cite);
        outOfTheRace = true;
        modifiers.push({ name: 'team-transfer', value: weeks, note: 'weeks until he signs with a new team' });
        break;
      }
      case 'pole':
      case 'poleOrHighest': {
        modifiers.push({ name: 'pole-result', value: effect.type });
        break;
      }
      case 'allDriversPlacement': {
        state.badWeather = true;
        modifiers.push({ name: 'bad-weather', value: 'qualifying cancelled' });
        break;
      }
      case 'altercation': {
        partnerId = drawPartner(state, driverId, ctx);
        modifiers.push({
          name: 'altercation',
          value: partnerId ? nameOf(state, partnerId) : 'no opponent available',
          note: effect.partner as string,
        });
        break;
      }
      default:
        break;
    }
  }

  state.tvResults.push({
    driverId,
    chain,
    chart,
    code,
    text: entry.text,
    placement,
    blue,
    drawnAsPartner,
  });

  push(state, {
    eventType: 'race-week',
    momentTag: `raceweek.${chart}`,
    ruleCitation: cite,
    actorIds: partnerId ? [driverId, partnerId] : [driverId],
    positionDeltas: [],
    modifiers,
    dice: diceRecords,
    description: `${nameOf(state, driverId)} — ${entry.text}`,
  });

  // The altercation: both drivers on the FEUD list, both take the placement.
  if (partnerId) {
    recordFeud(state, driverId, partnerId, cite);
    for (const effect of entry.effects) {
      if (effect.type === 'fine' && effect.appliesTo === 'both') recordFine(state, partnerId, effect, chart, code);
    }
    state.tvResults.push({
      driverId: partnerId,
      chain,
      chart,
      code,
      text: entry.text,
      placement,
      blue,
      drawnAsPartner: true,
    });
  }

  // The POLE readings (TV EVENT X 21/41, TV EVENT Z 11/22/33/44/55/66) print a
  // seat instead of a PLACEMENT letter.
  const wantsPole = entry.effects.some((e) => e.type === 'pole' || e.type === 'poleOrHighest');

  if (outOfTheRace) return;
  if (state.badWeather) return; // every placement is about to be overridden

  if (wantsPole) {
    takePoleResult(state, driverId, cite, entry.text);
  } else if (placement) {
    commitPlacement(state, driverId, placement, blue, cite, entry.text);
  }

  if (partnerId && !state.unavailable.has(partnerId) && placement) {
    // The blue die is re-read for the partner's own box: one throw seats one
    // card, and two drivers cannot both be put in box 4.
    const partnerBlue = state.dice.d6();
    commitPlacement(state, partnerId, placement, partnerBlue, cite, entry.text, [
      {
        kind: 'd6',
        dice: [partnerBlue],
        label: 'blue die (PLACEMENT box, altercation partner)',
        faces: { blue: partnerBlue },
      },
    ]);
  }
}

/**
 * RWEG/p.6 rows 31 and 42 — "[next drawn TV driver]". The TV list is drawn in
 * order, so the opponent is the next card off the stack. If the altercation is
 * on the last card drawn, one more is drawn and joins the TV list.
 *
 * AMBIGUITY (D-RW-10): only rows 31 and 42 name the opponent this way. TV
 * EVENT X 32, TV EVENT Y 13/43/63 say "other driver" and TV EVENT Z 26/56 say
 * "[choice] opponent" — a tabletop choice with no printed procedure. All of
 * them use the same mechanic here, because it is the only opponent-selection
 * rule the booklet prints and inventing a second one would be worse.
 */
function drawPartner(state: WeekState, driverId: string, ctx: TvContext): string | null {
  for (let k = ctx.index + 1; k < ctx.list.length; k++) {
    const candidate = ctx.list[k];
    if (candidate === driverId) continue;
    if (ctx.resolved.has(candidate)) continue;
    if (state.unavailable.has(candidate)) continue;
    ctx.resolved.add(candidate);
    return candidate;
  }
  // The altercation was on the last card drawn: draw one more.
  while (ctx.cursor.i < ctx.stack.length) {
    const candidate = ctx.stack[ctx.cursor.i++];
    if (candidate === driverId || state.unavailable.has(candidate)) continue;
    ctx.list.push(candidate);
    ctx.resolved.add(candidate);
    if (!state.tvIds.includes(candidate)) state.tvIds.push(candidate);
    state.categories.set(candidate, 'tv');
    push(state, {
      eventType: 'race-week',
      momentTag: 'raceweek.tv-list.extra-draw',
      ruleCitation: CITE_TV_LIST,
      actorIds: [candidate],
      positionDeltas: [],
      modifiers: [{ name: 'drawn-for-altercation', value: nameOf(state, candidate) }],
      dice: [],
      description: `${nameOf(state, candidate)} is drawn onto the TV LIST as the other half of the altercation.`,
    });
    return candidate;
  }
  return null;
}

function takePoleResult(state: WeekState, driverId: string, cite: string, text: string): void {
  const ref = firstFreeSeatFrom(state.board, 'pole', state.bottomCap);
  if (!ref) {
    // Every one of the 43 seats is taken. RWEG/p.4's "next-best available
    // position" has run out, so the bubble is all that is left.
    applyDestination(state.board, { group: 'bubble', top: false }, driverId);
    forfeitBannedChips(state, driverId, { group: 'bubble', top: false }, cite);
    state.placed.add(driverId);
    push(state, {
      eventType: 'race-week',
      momentTag: 'raceweek.placement.pole',
      ruleCitation: `${cite} + ${CITE_GROUP_FULL}`,
      actorIds: [driverId],
      positionDeltas: [{ driverId, from: 0, to: null, note: 'the bubble stack' }],
      modifiers: [
        { name: 'intended', value: 'POLE' },
        { name: 'group-full', value: 'the bubble stack', note: 'the whole field was already seated' },
      ],
      dice: [],
      description: `${nameOf(state, driverId)} qualified for the POLE but the field was full; he is on the bubble.`,
    });
    return;
  }
  seat(state.board, ref, driverId);
  state.placed.add(driverId);
  push(state, {
    eventType: 'race-week',
    momentTag: 'raceweek.placement.pole',
    ruleCitation: `${cite} + ${CITE_GROUP_FULL}`,
    actorIds: [driverId],
    positionDeltas: [
      { driverId, from: 0, to: null, placement: ref.group === 'pole' ? 'lead' : undefined, note: describeSeat(ref) },
    ],
    modifiers: [
      { name: 'intended', value: 'POLE' },
      { name: 'seated', value: describeSeat(ref) },
    ],
    dice: [],
    description:
      ref.group === 'pole'
        ? `${nameOf(state, driverId)} takes the POLE. ${text}`
        : `${nameOf(state, driverId)} wanted the POLE but it was gone; he starts from ${describeSeat(ref)}.`,
  });
}

// ---------------------------------------------------------------------------
// Committing a PLACEMENT
// ---------------------------------------------------------------------------

function commitPlacement(
  state: WeekState,
  driverId: string,
  letter: PlacementLetter,
  blue: number,
  cite: string,
  text: string,
  extraDice: DiceRecord[] = [],
): void {
  if (state.placed.has(driverId)) return;
  const trophy = trophyOf(state, driverId);
  const outcome = resolvePlacement(state.board, letter, trophy, blue, state.bottomCap, {
    rcSecondOnGrid: rcAlreadyGold(state, driverId),
  });
  applyDestination(state.board, outcome.destination, driverId);
  forfeitBannedChips(state, driverId, outcome.destination, cite);
  state.placed.add(driverId);
  state.placements.set(driverId, letter);

  // RWEG/p.4 — the FUME list. The booklet leaves it to taste and names only
  // "TV LIST CHART results 13, 62 or similar".
  //
  // RULING (D-RW-11): "or similar" is made deterministic — a driver goes on the
  // FUME list when his week ends at PLACEMENT D, which is exactly the set of
  // readings that either strand him on the bubble or throw him out of the race.
  // Both of the booklet's own examples (TV LIST 13 and 62) are PLACEMENT D, so
  // the rule reproduces the printed examples rather than replacing them.
  // Excluded: anyone injured, suspended, dead or otherwise not racing (he is
  // not there to fume), and anyone whose result generated a FEUD — a feud is
  // the stronger relationship and takes precedence.
  if (letter === 'D' && !state.unavailable.has(driverId) && !state.feudIds.has(driverId)) {
    state.fumeCandidates.add(driverId);
  }

  const isDnq = outcome.destination.group === 'dnq';
  push(state, {
    eventType: 'race-week',
    momentTag: `raceweek.placement.${letter.toLowerCase()}`,
    ruleCitation: outcome.fellBack ? `${cite} + ${CITE_PLACEMENT} + ${CITE_GROUP_FULL}` : `${cite} + ${CITE_PLACEMENT}`,
    actorIds: [driverId],
    positionDeltas: [
      {
        driverId,
        from: 0,
        to: null,
        placement: isDnq ? 'out-of-race' : placementTagFor(outcome.destination),
        note: outcome.where,
      },
    ],
    modifiers: [
      { name: 'placement', value: letter },
      { name: 'trophy', value: trophy ?? 'none' },
      { name: 'blue-die', value: blue, note: blueMattered(letter, trophy) ? 'selects the box' : 'no effect on this reading' },
      { name: 'intended', value: outcome.intended },
      ...(outcome.fellBack ? [{ name: 'group-full', value: outcome.where, note: 'next-best available position' } as Modifier] : []),
    ],
    dice: extraDice,
    description:
      `PLACEMENT ${letter} — ${nameOf(state, driverId)} (${trophy ?? 'no'} trophy) goes to ${outcome.where}. ${text}`,
  });
}

/** RWEG/p.3 — "in some TV LIST placement results, the blue die roll will have no effect." */
function blueMattered(letter: PlacementLetter, trophy: Trophy): boolean {
  if (letter === 'A') return true;
  if (letter === 'B') return trophy !== null;
  if (letter === 'C') return trophy !== null;
  return false;
}

function placementTagFor(destination: Destination): PositionDelta['placement'] {
  if (destination.group === 'pole') return 'lead';
  if (destination.group === 'top') return 'top-group';
  if (destination.group === 'middle') return 'middle-group';
  if (destination.group === 'bottom') return 'end-of-bottom';
  return undefined;
}

// ---------------------------------------------------------------------------
// STEPS THREE / FOUR / FIVE
// ---------------------------------------------------------------------------

function runDealStep(
  state: WeekState,
  stack: string[],
  cursor: { i: number },
  step: 'fast' | 'slow',
  cite: string,
  label: string,
): void {
  const roll = state.dice.sum2d6();
  const drawn: string[] = [];
  for (let n = 0; n < roll.total && cursor.i < stack.length; n++) {
    const id = stack[cursor.i++];
    if (state.unavailable.has(id) || state.placed.has(id)) {
      n--;
      continue;
    }
    drawn.push(id);
    state.categories.set(id, step);
  }

  push(state, {
    eventType: 'race-week',
    momentTag: `raceweek.${step}-week.count`,
    ruleCitation: cite,
    actorIds: drawn,
    positionDeltas: [],
    modifiers: [{ name: `${step}-week-drivers`, value: drawn.length }],
    dice: [
      {
        kind: '2d6-sum',
        dice: roll.dice,
        total: roll.total,
        label: `${label} count (metallic dice)`,
        flags: { doubles: roll.doubles },
      },
    ],
    description: `${roll.total} drivers had a ${label.toLowerCase()}.`,
  });

  for (const id of drawn) placeNoRoll(state, id, step, cite);
}

function runUneventfulStep(state: WeekState, stack: string[], cursor: { i: number }): void {
  const remaining: string[] = [];
  for (; cursor.i < stack.length; cursor.i++) {
    const id = stack[cursor.i];
    if (state.unavailable.has(id) || state.placed.has(id)) continue;
    remaining.push(id);
    state.categories.set(id, 'uneventful');
  }
  push(state, {
    eventType: 'race-week',
    momentTag: 'raceweek.uneventful.count',
    ruleCitation: CITE_UNEVENTFUL,
    actorIds: remaining,
    positionDeltas: [],
    modifiers: [{ name: 'uneventful-week-drivers', value: remaining.length }],
    dice: [],
    description: `${remaining.length} drivers had an uneventful week. No die roll is necessary.`,
  });
  for (const id of remaining) placeNoRoll(state, id, 'uneventful', CITE_UNEVENTFUL);
}

function placeNoRoll(
  state: WeekState,
  driverId: string,
  step: 'fast' | 'slow' | 'uneventful',
  cite: string,
): void {
  if (state.placed.has(driverId)) return;
  const trophy = trophyOf(state, driverId);
  const outcome = placeByTrophy(state.board, step, trophy, state.bottomCap);
  applyDestination(state.board, outcome.destination, driverId);
  forfeitBannedChips(state, driverId, outcome.destination, cite);
  state.placed.add(driverId);

  push(state, {
    eventType: 'race-week',
    momentTag: `raceweek.${step}-week.place`,
    ruleCitation: outcome.fellBack ? `${cite} + ${CITE_GROUP_FULL}` : cite,
    actorIds: [driverId],
    positionDeltas: [
      {
        driverId,
        from: 0,
        to: null,
        placement: outcome.destination.group === 'dnq' ? 'out-of-race' : placementTagFor(outcome.destination),
        note: outcome.where,
      },
    ],
    modifiers: [
      { name: 'trophy', value: trophy ?? 'none' },
      { name: 'intended', value: outcome.intended },
      ...(outcome.fellBack ? [{ name: 'group-full', value: outcome.where, note: 'next-best available position' } as Modifier] : []),
    ],
    dice: [],
    description: `${nameOf(state, driverId)} (${trophy ?? 'no'} trophy) goes to ${outcome.where}.`,
  });
}

// ---------------------------------------------------------------------------
// Bad weather
// ---------------------------------------------------------------------------

/**
 * TV EVENT Z 21/41/61 — "Bad weather cancels qualifying; ALL drivers,
 * PLACEMENT C."
 *
 * Every placement made so far is thrown out and every driver who can still race
 * takes PLACEMENT C: a trophy driver gets the blue die's box in the MIDDLE
 * group, a non-trophy driver goes to the top of the bubble stack. STEPS THREE,
 * FOUR and FIVE do not run — there was no qualifying to have a fast or a slow
 * week in.
 *
 * What SURVIVES: fines, injuries, suspensions, deaths and feuds generated
 * earlier in the week. Those happened during race week, not during qualifying,
 * and the weather does not undo them.
 */
function applyBadWeather(state: WeekState, rosterOrder: readonly string[]): void {
  const before = boardOrder(state.board).length;
  state.board.pole = null;
  state.board.top.fill(null);
  state.board.middle.fill(null);
  state.board.bottom = [];
  state.board.bubble = [];
  state.board.dnq = [];
  state.placed.clear();
  state.placements.clear();
  // The FUME list is derived from the PLACEMENT a week ended at, and every
  // placement is about to become C, so nobody arrives at the green flag fuming
  // over a qualifying result that never happened. FEUDS are not cleared: an
  // altercation during the week is not a qualifying result.
  //
  // AMBIGUITY (D-RW-12): the booklet does not say whether chips earned in
  // cancelled qualifying survive. They are left standing here — the printed
  // reading that granted them ("HOT week in hot laps", "team cohesion") is a
  // race-week event like the fines and the feuds, not a qualifying time — and
  // the BOTTOM/bubble ban still strips them from anyone the rain puts there.
  state.fumeCandidates.clear();

  push(state, {
    eventType: 'race-week',
    momentTag: 'raceweek.bad-weather',
    ruleCitation: 'RWEG/p.10 — TV EVENT Z 21 / 41 / 61',
    actorIds: [],
    positionDeltas: [],
    modifiers: [
      { name: 'placements-voided', value: before, note: 'drivers already placed are overridden' },
      { name: 'all-drivers', value: 'PLACEMENT C' },
      { name: 'stands', value: 'fines, injuries, suspensions, feuds', note: 'generated during the week, not qualifying' },
    ],
    dice: [],
    description:
      'Bad weather cancels qualifying. Every placement already made is thrown out and the whole field takes ' +
      'PLACEMENT C. The fines, injuries, suspensions and feuds of the week still stand.',
  });

  for (const id of rosterOrder) {
    if (state.unavailable.has(id)) continue;
    const blue = state.dice.d6();
    commitPlacement(state, id, 'C', blue, 'RWEG/p.10 — TV EVENT Z bad weather', 'Bad weather cancels qualifying.', [
      { kind: 'd6', dice: [blue], label: 'blue die (PLACEMENT box, bad weather)', faces: { blue } },
    ]);
    if (!state.categories.has(id)) state.categories.set(id, 'uneventful');
  }
}

// ---------------------------------------------------------------------------
// STEP SIX and the two clarifications
// ---------------------------------------------------------------------------

/**
 * RWEG/p.4 STEP SIX — "Count the number of drivers in the TOP, MIDDLE, and
 * BOTTOM groups. Determine how many drivers from the 'bubble stack' are
 * necessary to bring the field to 43 drivers, then count out that many drivers,
 * starting with the FIRST (highest-ranking) driver in the 'bubble stack.'
 * Place those drivers at the end of the BOTTOM group."
 */
function roundOutTheField(state: WeekState): void {
  const before = boardOrder(state.board).length;
  const need = Math.max(0, state.fieldSize - before);
  const taken: string[] = [];
  while (taken.length < need && state.board.bubble.length > 0 && state.board.bottom.length < state.bottomCap) {
    const id = state.board.bubble.shift()!;
    state.board.bottom.push(id);
    taken.push(id);
  }
  const left = [...state.board.bubble];

  push(state, {
    eventType: 'race-week',
    momentTag: 'raceweek.round-out',
    ruleCitation: CITE_ROUND_OUT,
    actorIds: taken,
    positionDeltas: taken.map((id) => ({ driverId: id, from: 0, to: null, placement: 'end-of-bottom' as const })),
    modifiers: [
      { name: 'field-before', value: before },
      { name: 'called-off-the-bubble', value: taken.length },
      { name: 'left-on-the-bubble', value: left.length, note: 'these drivers do not race' },
    ],
    dice: [],
    description:
      taken.length > 0
        ? `${taken.length} driver${taken.length === 1 ? '' : 's'} come off the bubble to round the field out to ` +
          `${before + taken.length}: ${taken.map((id) => nameOf(state, id)).join(', ')}.`
        : `The field is already ${before} cars; nobody comes off the bubble.`,
  });

  if (left.length > 0) {
    push(state, {
      eventType: 'race-week',
      momentTag: 'raceweek.bubble.did-not-race',
      ruleCitation: CITE_ROUND_OUT,
      actorIds: left,
      positionDeltas: left.map((id) => ({ driverId: id, from: 0, to: null, placement: 'out-of-race' as const })),
      modifiers: [{ name: 'left-on-the-bubble', value: left.length }],
      dice: [],
      description: `${left.map((id) => nameOf(state, id)).join(', ')} stay on the bubble and do not race.`,
    });
  }

  if (state.board.dnq.length > 0) {
    push(state, {
      eventType: 'race-week',
      momentTag: 'raceweek.dnq',
      ruleCitation: CITE_PLACEMENT,
      actorIds: [...state.board.dnq],
      positionDeltas: state.board.dnq.map((id) => ({
        driverId: id,
        from: 0,
        to: null,
        placement: 'out-of-race' as const,
      })),
      modifiers: [{ name: 'did-not-qualify', value: state.board.dnq.length }],
      dice: [],
      description: `${state.board.dnq.map((id) => nameOf(state, id)).join(', ')} failed to qualify.`,
    });
  }
}

/**
 * RWEG/p.4 — "it's possible that there may be open spaces in the TOP and/or
 * MIDDLE groups. If this is the case, simply move up the highest-ranked drivers
 * from the lower groups to fill those spots."
 *
 * The pole is included: a week in which nobody drew a POLE result would
 * otherwise start the race with an empty LEADER space.
 */
function compactOpenSpaces(state: WeekState): void {
  const board = state.board;
  const moves: PositionDelta[] = [];

  const promoteInto = (take: () => string | null, place: (id: string) => void, into: string): void => {
    const id = take();
    if (!id) return;
    place(id);
    moves.push({ driverId: id, from: 0, to: null, note: `moved up into ${into}` });
  };

  // The highest-ranked driver still in a group below the one being filled.
  const takeFromBelow = (from: 'top' | 'middle' | 'bottom'): string | null => {
    if (from === 'top') {
      const i = board.top.findIndex((x) => x !== null);
      if (i !== -1) {
        const id = board.top[i]!;
        board.top[i] = null;
        return id;
      }
      return takeFromBelow('middle');
    }
    if (from === 'middle') {
      const i = board.middle.findIndex((x) => x !== null);
      if (i !== -1) {
        const id = board.middle[i]!;
        board.middle[i] = null;
        return id;
      }
      return takeFromBelow('bottom');
    }
    return board.bottom.length > 0 ? board.bottom.shift()! : null;
  };

  if (board.pole === null) {
    promoteInto(() => takeFromBelow('top'), (id) => (board.pole = id), 'the LEADER space');
  }
  for (let i = 0; i < board.top.length; i++) {
    if (board.top[i] !== null) continue;
    promoteInto(() => takeFromBelow('middle'), (id) => (board.top[i] = id), `the TOP group, box ${i + 1}`);
  }
  for (let i = 0; i < board.middle.length; i++) {
    if (board.middle[i] !== null) continue;
    promoteInto(() => takeFromBelow('bottom'), (id) => (board.middle[i] = id), `the MIDDLE group, seat ${i + 1}`);
  }

  if (moves.length > 0) {
    push(state, {
      eventType: 'race-week',
      momentTag: 'raceweek.open-spaces',
      ruleCitation: CITE_OPEN_SPACES,
      actorIds: moves.map((m) => m.driverId),
      positionDeltas: moves,
      modifiers: [{ name: 'promoted', value: moves.length, note: 'open spaces in TOP/MIDDLE filled from below' }],
      dice: [],
      description:
        `Qualifying left ${moves.length} open space${moves.length === 1 ? '' : 's'} up front; the highest-ranked ` +
        'drivers from the lower groups move up to fill them.',
    });
  }
}

// ---------------------------------------------------------------------------
// The chip ban
// ---------------------------------------------------------------------------

/**
 * RWEG/p.4 — "performances chips cannot be carried by drivers in the BOTTOM
 * group or 'bubble' stack. If a driver earns a performance chip from his TV
 * LIST dice roll, but his placement reading calls for him to be placed in the
 * BOTTOM group or 'bubble' stack, he forfiets his performance chip."
 */
function enforceChipBan(state: WeekState, grid: readonly string[]): void {
  const forfeits: ChipForfeit[] = [];
  for (const [id, n] of state.chips) {
    if (n <= 0) continue;
    const index = grid.indexOf(id);
    const inBottom = index === -1 || index + 1 >= 1 + TOP_SEATS + MIDDLE_SEATS + 1;
    if (!inBottom) continue;
    forfeits.push({
      driverId: id,
      chips: n,
      reason: index === -1 ? 'placed on the bubble stack or out of the race' : 'placed in the BOTTOM group',
      ruleCitation: CITE_CHIP_BAN,
    });
    state.chips.set(id, 0);
  }
  if (forfeits.length === 0) return;
  state.chipsForfeited.push(...forfeits);
  push(state, {
    eventType: 'race-week',
    momentTag: 'raceweek.chip-forfeit',
    ruleCitation: CITE_CHIP_BAN,
    actorIds: forfeits.map((f) => f.driverId),
    positionDeltas: [],
    modifiers: forfeits.map((f) => ({ name: 'chips-forfeited', value: -f.chips, note: `${nameOf(state, f.driverId)}: ${f.reason}` })),
    dice: [],
    description: forfeits
      .map((f) => `${nameOf(state, f.driverId)} forfeits ${f.chips} performance chip${f.chips === 1 ? '' : 's'} — ${f.reason}.`)
      .join(' '),
  });
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

function push(state: WeekState, event: RuleEvent): void {
  state.events.push(event);
}

function nameOf(state: WeekState, driverId: string): string {
  return state.byId.get(driverId)?.name ?? driverId;
}

function rollDiceSpec(state: WeekState, spec: DiceSpec, into: DiceRecord[], label: string): number {
  const faces: number[] = [];
  for (let i = 0; i < spec.count; i++) faces.push(state.dice.d(spec.sides));
  const total = faces.reduce((a, b) => a + b, 0);
  into.push({
    kind: spec.count === 1 ? 'd6' : 'd6-set',
    dice: faces,
    total,
    label: `${label} (${spec.count}d${spec.sides})`,
  });
  return total;
}

function recordFine(
  state: WeekState,
  driverId: string,
  effect: RaceWeekEffect,
  chart: ChartId,
  code: string,
): void {
  const payer = (effect.payer as 'driver' | 'team') ?? 'driver';
  state.fines.push({
    driverId,
    amount: (effect.amount as number | null) ?? null,
    currency: (effect.currency as string) ?? 'Mb',
    payer,
    offense: effect.offense as string,
    chart,
    code,
    ruleCitation: `${citeRow(chart, code)} + ${payer === 'driver' ? CITE_DRIVER_FINES : CITE_TEAM_FINES}`,
  });
}

function markUnavailable(
  state: WeekState,
  driverId: string,
  reason: UnavailableReason,
  text: string,
  chart: ChartId,
  code: string,
  ruleCitation: string,
): void {
  if (state.unavailable.has(driverId)) return;
  state.unavailable.set(driverId, { driverId, reason, text, chart, code, ruleCitation });
  state.fumeCandidates.delete(driverId);
  push(state, {
    eventType: 'race-week',
    momentTag: `raceweek.unavailable.${reason}`,
    ruleCitation,
    actorIds: [driverId],
    positionDeltas: [{ driverId, from: 0, to: null, placement: 'out-of-race', note: reason }],
    modifiers: [{ name: 'unable-to-race', value: reason }],
    dice: [],
    description: `${nameOf(state, driverId)} will not take the green flag — ${text}`,
  });
}

function recordFeud(state: WeekState, a: string, b: string, ruleCitation: string): void {
  const already = state.feuds.some((f) => (f.a === a && f.b === b) || (f.a === b && f.b === a));
  if (!already) state.feuds.push({ a, b });
  state.feudIds.add(a);
  state.feudIds.add(b);
  // A feud takes precedence over the FUME list (RWEG/p.4).
  state.fumeCandidates.delete(a);
  state.fumeCandidates.delete(b);
  push(state, {
    eventType: 'race-week',
    momentTag: 'raceweek.feud',
    ruleCitation: `${ruleCitation} + ${CITE_ALTERCATION} + ${CITE_FEUD_NOTE}`,
    actorIds: [a, b],
    positionDeltas: [],
    modifiers: [{ name: 'feud', value: `${nameOf(state, a)} v ${nameOf(state, b)}` }],
    dice: [],
    description: `${nameOf(state, a)} and ${nameOf(state, b)} go on the FEUD list.`,
  });
}

// ---------------------------------------------------------------------------
// Wiring race week into a race
// ---------------------------------------------------------------------------

export interface ToRaceSetupOptions {
  track: TrackCard | string;
  /** Defaults to the race week seed. */
  seed?: number;
  totalTurns?: number;
  raceId?: string;
  raceName?: string;
}

/**
 * Turn a finished race week into the `RaceSetup` that runs the race. The grid,
 * the TV list, the feuds and the fume list all come straight off the week.
 */
export function toRaceSetup(week: RaceWeekResult, options: ToRaceSetupOptions): RaceSetup {
  return {
    track: options.track,
    drivers: week.grid,
    seed: options.seed ?? week.seed,
    totalTurns: options.totalTurns,
    raceId: options.raceId,
    raceName: options.raceName,
    tvList: week.tvList,
    feuds: week.feuds,
    fuming: week.fuming,
  };
}

/**
 * Put race week's performance chips on the cards before the green flag.
 *
 * `RaceSetup` has no chip field — `buildStartingGrid` starts everybody on zero
 * — so chips earned in qualifying are applied to the built state here. The
 * BOTTOM-group ban is enforced a second time by `renumber`, which is belt and
 * braces: `runRaceWeek` already stripped them (RWEG/p.4).
 */
export function applyStartingChips(state: RaceState, chips: Record<string, number>): RaceState {
  const grid = state.grid.map((car) => {
    const n = chips[car.card.driverId];
    return n && n > 0 ? { ...car, chips: n } : car;
  });
  return { ...state, grid: renumber(grid).order };
}
